'use client';

import { useState } from 'react';
import { Image as ImageIcon, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/forms/button';
import { Label } from '@/components/ui/forms/label';
import { ProfilePictureDialog } from './ProfilePictureDialog';
import { isRenderableProfilePictureUrl, parseProfilePhotoUrl } from '@/lib/resume/profile-picture';

const UPLOAD_ENDPOINT = '/api/resume-builder/profile-picture';

interface ProfilePictureFieldProps {
  /** Current `personalInfo.profilePictureUrl`; may be any legacy URL. */
  value?: string;
  onChange: (url: string) => void;
  /**
   * Whether the resume's current template lays a photo out. Purely advisory —
   * the photo stays stored either way, so switching templates brings it back.
   */
  templateShowsPhoto: boolean;
}

/**
 * Upload button, thumbnail and Change/Remove actions — the replacement for the
 * raw "Profile Picture URL" text input.
 *
 * The stored value is still a URL string, so `ResumeContent` is unchanged and
 * a resume that already carries an external URL keeps rendering it; it simply
 * cannot be typed in any more.
 */
export function ProfilePictureField({ value, onChange, templateShowsPhoto }: ProfilePictureFieldProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasPhoto = Boolean(value?.trim());
  // Same screen the renderer applies, so the editor never previews a value the
  // resume itself would refuse to render.
  const previewUrl = isRenderableProfilePictureUrl(value) ? value.trim() : null;

  const handleConfirm = async (blob: Blob) => {
    const formData = new FormData();
    formData.append('file', blob, 'profile-photo.jpg');
    // Lets the server drop the object this one replaces.
    if (value) formData.append('previousUrl', value);

    const response = await fetch(UPLOAD_ENDPOINT, { method: 'POST', body: formData });
    const payload = (await response.json().catch(() => null)) as { url?: string; error?: string } | null;

    if (!response.ok || !payload?.url) {
      throw new Error(payload?.error || 'Failed to upload photo');
    }

    setError(null);
    onChange(payload.url);
  };

  const handleRemove = async () => {
    if (!value) return;
    setRemoving(true);
    setError(null);
    try {
      // Only our own objects can be deleted; an external URL just gets cleared.
      if (parseProfilePhotoUrl(value)) {
        await fetch(UPLOAD_ENDPOINT, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: value }),
        });
      }
      onChange('');
    } catch {
      setError('Could not remove the photo. Please try again.');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label>Profile Photo</Label>

      {/*
        One card: the thumbnail, a label, and the actions read as a single
        control. Clicking anywhere opens the editor; the explicit buttons do the
        same thing for anyone who wants a target to aim at.

        A <div role="button"> rather than a <button>, because the actions nested
        inside are buttons themselves and a button inside a button is invalid
        HTML that browsers reparent — which silently breaks the click target.
        Each nested action stops propagation so it never also opens the dialog.

        Sizing note: explicit `h-14 w-14` rather than `size-14`. The image box
        MUST have a resolved height and width — the <img> inside is `h-full
        w-full`, so if the box has no size the percentage resolves against
        nothing and the element falls back to the photo's intrinsic dimensions,
        blowing the card out to full width.
      */}
      <div
        role="button"
        tabIndex={0}
        aria-label={hasPhoto ? 'Change profile photo' : 'Upload profile photo'}
        onClick={() => setDialogOpen(true)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setDialogOpen(true);
          }
        }}
        className="flex w-full cursor-pointer items-center gap-3 border border-input bg-background p-3 text-left transition-colors hover:border-border/80 hover:bg-muted/30 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/10"
      >
        <div className="h-14 w-14 flex-none overflow-hidden border border-border bg-muted">
          {previewUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={previewUrl} alt="" className="h-14 w-14 object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-muted-foreground">
              <ImageIcon className="h-4 w-4" />
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="truncate text-body-sm text-foreground">
            {hasPhoto ? 'Profile photo' : 'No photo yet'}
          </p>
          <p className="truncate text-caption text-muted-foreground">
            {hasPhoto ? 'Click to reposition or replace' : 'Click to add and position a photo'}
          </p>
        </div>

        <div className="flex flex-none items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={removing}
            onClick={(event) => {
              event.stopPropagation();
              setDialogOpen(true);
            }}
          >
            <Pencil className="mr-1.5 h-3.5 w-3.5" />
            {hasPhoto ? 'Change' : 'Upload'}
          </Button>
          {hasPhoto && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={removing}
              onClick={(event) => {
                event.stopPropagation();
                void handleRemove();
              }}
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              {removing ? 'Removing…' : 'Remove'}
            </Button>
          )}
        </div>
      </div>

      <p className="text-caption text-muted-foreground">
        {hasPhoto && !templateShowsPhoto
          ? 'Optional. Saved, but the template you have selected does not print a photo — pick a photo-friendly template to show it.'
          : 'Optional. Shown by templates that lay out a photo; ATS-first and minimal templates leave it off.'}
      </p>

      {error && <p className="text-caption text-destructive">{error}</p>}

      <ProfilePictureDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onConfirm={handleConfirm}
        // Opens on the photo already saved, so the common action is repositioning
        // rather than re-picking the same file.
        initialImageUrl={previewUrl}
      />
    </div>
  );
}
